/**
 * Exact decimal arithmetic on the strings the API sends.
 *
 * `formatMoney` exists so an amount is never *parsed* on its way to the screen.
 * This is the other half: the few places that must compute one — a line
 * amount previewed as somebody types, the tax under a draft — and cannot do it
 * in `Number` without being wrong.
 *
 * Wrong in a specific, demonstrable way. The server computes
 * `money(quantity * unit_rate)` in `Decimal`, rounding halves away from zero.
 * A client doing the same in binary floating point disagrees with it on
 * ordinary invoice inputs, because a decimal like 1.005 has no exact binary
 * form and lands a hair under the half it is supposed to round up from:
 *
 * | quantity x rate | `(q * r).toFixed(2)` | `Math.round(q * r * 100) / 100` | server |
 * | --------------- | -------------------- | ------------------------------- | ------ |
 * | 1.005 x 1.00    | 1.00                 | 1.00                            | 1.01   |
 * | 2.5 x 1.01      | 2.52                 | 2.53                            | 2.53   |
 * | 0.015 x 1.00    | 0.01                 | 0.02                            | 0.02   |
 * | 3.335 x 3.00    | 10.00                | 10.00                           | 10.01  |
 *
 * A preview whose whole purpose is to show the rounding before it happens must
 * not be the thing that gets it wrong.
 *
 * So the digits are read into a `bigint` and a scale, and every operation is
 * integer arithmetic. `bigint` rather than `number` because the intermediate of
 * a multiplication carries the sum of both scales — five places for a quantity
 * at three and a rate at two — and a large invoice would otherwise reach the
 * edge of what a double holds exactly.
 *
 * **Still not for a total that matters.** Anything the business acts on is
 * computed by the server and read back. These are for what is on screen before
 * a save.
 */

/** Two places, everywhere, as on the server. */
const MONEY_SCALE = 2;

interface Scaled {
  /** The digits, sign included, as an integer. */
  units: bigint;
  /** How many of those digits are after the point. */
  scale: number;
}

/** Read a decimal string. `null` for anything that is not one — including ''. */
function parse(value: string | null | undefined): Scaled | null {
  if (value === null || value === undefined) return null;
  const text = value.trim();
  // Deliberately strict. A partially typed box holds `-`, `1.`, or `1.2.3`, and
  // guessing at those is how a preview shows a number for something that is not
  // one yet. The caller decides what an unreadable value means on screen.
  if (!/^[+-]?\d+(\.\d+)?$/.test(text)) return null;
  const negative = text.startsWith('-');
  const digits = text.replace(/^[+-]/, '');
  const [whole, fraction = ''] = digits.split('.');
  const units = BigInt(whole + fraction);
  return { units: negative ? -units : units, scale: fraction.length };
}

function pow10(n: number): bigint {
  return 10n ** BigInt(n);
}

/** Re-scale, rounding halves AWAY FROM ZERO — the server's `ROUND_HALF_UP`. */
function rescale({ units, scale }: Scaled, target: number): bigint {
  if (scale <= target) return units * pow10(target - scale);
  const divisor = pow10(scale - target);
  const quotient = units / divisor; // bigint division truncates toward zero
  const remainder = units % divisor; // and the remainder keeps the sign
  const absRemainder = remainder < 0n ? -remainder : remainder;
  if (absRemainder * 2n >= divisor) {
    return quotient + (units < 0n ? -1n : 1n);
  }
  return quotient;
}

/** An integer count of hundredths back to `-1234.56`. */
function render(units: bigint): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(MONEY_SCALE + 1, '0');
  const whole = digits.slice(0, -MONEY_SCALE);
  const fraction = digits.slice(-MONEY_SCALE);
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/**
 * `quantity * unitRate`, to two places — what the server will store as the
 * line's amount. `null` if either side is not a decimal number yet.
 */
export function multiplyMoney(quantity: string, unitRate: string): string | null {
  const a = parse(quantity);
  const b = parse(unitRate);
  if (!a || !b) return null;
  return render(rescale({ units: a.units * b.units, scale: a.scale + b.scale }, MONEY_SCALE));
}

/**
 * The sum of amounts, to two places.
 *
 * An unreadable entry contributes nothing rather than poisoning the total:
 * these are summed while somebody is still typing into one of the boxes.
 */
export function sumMoney(values: readonly (string | null | undefined)[]): string {
  let total = 0n;
  for (const value of values) {
    const parsed = parse(value);
    if (parsed) total += rescale(parsed, MONEY_SCALE);
  }
  return render(total);
}

/**
 * `percent` of `amount`, to two places — the server's
 * `money(taxable * tax_rate / 100)`.
 */
export function percentOfMoney(amount: string, percent: string): string {
  const base = parse(amount);
  const rate = parse(percent);
  if (!base || !rate) return render(0n);
  return render(
    // Divided by 100 by adding two to the scale rather than by dividing, so the
    // only rounding is the single one at the end — as on the server, where the
    // whole expression is quantised once.
    rescale({ units: base.units * rate.units, scale: base.scale + rate.scale + 2 }, MONEY_SCALE),
  );
}

/** `a + b`, to two places. */
export function addMoney(a: string, b: string): string {
  return sumMoney([a, b]);
}
